import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type RefObject,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle2,
  Circle,
  CopyPlus,
  Leaf,
  MoreHorizontal,
  Search,
  UserPlus,
  X,
} from 'lucide-react';
import {
  ApiError,
  cardDescriptionDraftPutSchema,
  cardDescriptionDraftSchema,
  cardFieldOverridePayloadSchema,
  type BoardContents,
  type BoardContentsBoard,
  type BoardMemberWithHistory,
  type AutomationButtonRunReceipt,
  type CardDescriptionDraft,
  type CardFieldOverridePayload,
  type Reconciliation,
} from '@molt/shared';
import {
  Avatar,
  AvatarStack,
  Button,
  Dialog,
  EmptyState,
  FacetOption,
  IconButton,
  InlineEdit,
  LabelChip,
  MarkdownBody,
  Menu,
  Notice,
  Popover,
  Skeleton,
  TextField,
  profileName,
  type MenuSection,
} from '@/components';
import { announce } from '@/lib/announce';
import { del, get, put } from '@/lib/api';
import { boardContentsQueryOptions } from '@/lib/board-face';
import { parseCardTimestamp } from '@/lib/card-dates';
import { cn } from '@/lib/cn';
import { listColourStyle, listColourVar } from '@/lib/list-colours';
import {
  hasReconciliationConsequences,
  useMoveDestinations,
} from '@/lib/cross-board-move';
import { useCopyCard } from '@/lib/card-copy';
import { useConvertLinkCard, useCreateCardMirror } from '@/lib/card-mirrors';
import { cardEmbedUrl, cardExportQueryOptions, useMergeCards } from '@/lib/card-share';
import { useInterfaceLanguage } from '@/lib/interface-language';
import {
  cardTemplateQueryOptions,
  useCreateCardFromTemplate,
  useHideCardTemplateFromList,
  useMarkCardTemplate,
  useUnhideCardTemplateFromList,
  useUnmarkCardTemplate,
} from '@/lib/card-templates';
import {
  cardDetailQueryOptions,
  cardFieldVersionsQueryOptions,
  previewCardMoveToBoard,
  renameCard,
  useArchiveCardDetail,
  useMoveCard,
  useMoveCardToBoard,
  useDeleteCard,
  useAddCardMember,
  useRemoveCardMember,
  useRestoreCard,
  useSetCardCompleted,
  useUpdateCardDescription,
} from '@/lib/cards';
import {
  useCardLive,
  type CardLiveSendMessage,
  type CardLiveStatus,
} from '@/lib/card-live';
import {
  loadOfflineDescriptionDrafts,
  saveOfflineDescriptionDrafts,
  type OfflineDescriptionDraft,
} from '@/lib/offline-sync';
import type { LiveSocketFrame } from '@/lib/socket';
import { useRemoveCardLabel } from '@/lib/labels';
import { relatedCardsQueryOptions } from '@/lib/related-cards';
import { boardListsQueryOptions } from '@/lib/lists';
import { queryKeys } from '@/lib/query-keys';
import { useSetCardEvergreen } from '@/lib/card-aging';
import { typingMessage, useCommentTypingIndicators } from '@/lib/comment-typing';
import { formatLocaleDateTimeLabel } from '@/lib/locale-formatting';
import { useLocalePreferencesValue } from '@/lib/locale-preferences';
import { labelDisplayName } from '@/lib/label-display';
import { useUndoRedo } from '@/lib/undo';
import { useCardAutomationButtons, useRunCardAutomationButton } from '@/lib/automations';
import { commentStreamQueryOptions } from '@/lib/comments';
import {
  useEmailToCardAddress,
  useRegenerateEmailToCardAddress,
} from '@/lib/email-to-card';
import {
  powerUpSurfaceSummary,
  useBoardPowerUpSurfaces,
  type PowerUpSurfaceRuntimeEntry,
  type PowerUpSurfaceSummary,
} from '@/lib/power-ups';
import { AutomationButtonStrip } from '@/components/automation/AutomationButtonStrip';
import { SharePanel, type SharePanelItem } from '@/components/share';
import {
  ArchiveDeleteConfirm,
  type ArchiveDeleteTarget,
} from '../board/ArchiveDeleteConfirm';
import { ArchiveRestoreDestinationDialog } from '../board/ArchiveRestoreDestinationDialog';
import { CardCopyDialog } from '../board/CardCopyDialog';
import { CardTemplateCreateDialog } from '../board/CardTemplateCreateDialog';
import { CrossBoardMoveDialog } from '../board/CrossBoardMoveDialog';
import { MirrorCardDialog } from '../board/MirrorCardDialog';
import { TemplateCardVisibilityAction } from '../board/TemplateCardVisibilityAction';
import { WatchToggle } from '../watch/WatchToggle';
import { LabelPicker } from './LabelPicker';
import { CommentSection } from './CommentSection';
import { CardDatesSection } from './CardDatesSection';
import { CardCoverSection } from './CardCoverSection';
import { CardStickerSection } from './CardStickerSection';
import { CardLocationSection } from './CardLocationSection';
import { CardVoteSection } from './CardVoteSection';
import { CardApprovalSection } from './CardApprovalSection';
import { CardCustomFieldsSection } from './CardCustomFieldsSection';
import { CardChecklistSection } from './CardChecklistSection';
import { CardAttachmentSection } from './CardAttachmentSection';
import { RelatedCardsSection, type RelatedCardOption } from './RelatedCardsSection';
import { CardConflictNotice } from './CardConflictNotice';
import { CardPrintView } from './CardPrintView';
import { CardMergeDialog, type CardMergeCandidate } from './CardMergeDialog';
import { CardReferenceCopy } from './CardReferenceCopy';
import { CardEmailToCardSection } from '../integrations/CardEmailToCardSection';
import { RichDescriptionEditor, cardDescriptionDraftKey } from './RichDescriptionEditor';

const DESCRIPTION_MAX_LENGTH = 16_384;
const DESCRIPTION_WARN_AT = 15_500;
const DESCRIPTION_RETRY_BACKOFF_MS = 30_000;

/**
 * How long the description editor must sit idle before the unsaved text is
 * mirrored to the account — `FE-14.2`, `FR-CARD-003` AC2.
 *
 * ⚠ **The mirror is a per-PAUSE write, never a per-keystroke one**, and that is
 * the whole reason `B14` declined to build this: `routes/card-detail.ts` warned
 * that "a draft and a save are different intentions" and
 * `packages/shared/src/events.ts` that "a keystroke-rate event on an
 * append-only table is a different animal from an explicit Save". The route
 * this debounce feeds emits **no event at all**, so the cost of a write is one
 * upsert of one private row and nothing else — but a `PUT` per character would
 * still be a `PUT` per character.
 *
 * Exported so `CardBack.test.tsx` advances its fake clock by this constant
 * rather than by a literal: a hard-coded number in the test would keep passing
 * while silently testing nothing the day the delay is tuned.
 */
export const DESCRIPTION_DRAFT_MIRROR_DEBOUNCE_MS = 800;

/**
 * `GET`/`PUT`/`DELETE /cards/:id/description/draft` — the caller's own unsaved
 * description text for one card (`FE-14.2`, `FR-CARD-003` AC2, the Card-Back
 * State contract chunk).
 *
 * ⚠ **These three live in this file rather than in a new `@/lib/` module**, and
 * that is a scope decision rather than a preference: this chunk's declared web
 * file list is `CardBack.tsx` and `RichDescriptionEditor.tsx`, and eleven
 * sibling chunks are editing the same tree. `RichDescriptionEditor.tsx` already
 * exports `cardDescriptionDraftKey` from a component file, so the neighbourhood
 * has the precedent. A follow-up that owns `apps/web/src/lib/` should lift them
 * into a module beside `cards.ts`.
 */
function cardDescriptionDraftPath(cardId: string): string {
  return `/cards/${cardId}/description/draft`;
}

function fetchCardDescriptionDraft(cardId: string): Promise<CardDescriptionDraft> {
  return get(cardDescriptionDraftPath(cardId), cardDescriptionDraftSchema);
}

/**
 * ⚠ **`cardDescriptionDraftPutSchema` is `.min(1)`**, so this parse throws on
 * `''` rather than sending it. That is deliberate — see
 * `mirrorDescriptionDraft` below, which routes an emptied field to the `DELETE`
 * instead, because `draft_text` is `NOT NULL` and "no draft" is "no row".
 */
function storeCardDescriptionDraft(
  cardId: string,
  draftText: string,
): Promise<CardDescriptionDraft> {
  return put(
    cardDescriptionDraftPath(cardId),
    cardDescriptionDraftSchema,
    cardDescriptionDraftPutSchema.parse({ draftText }),
  );
}

/** Idempotent by contract: a card with no stored draft answers 200, not 404. */
function discardCardDescriptionDraft(cardId: string): Promise<CardDescriptionDraft> {
  return del(cardDescriptionDraftPath(cardId), cardDescriptionDraftSchema);
}

function isNetworkSaveError(error: unknown): boolean {
  return (
    error instanceof ApiError &&
    error.code === 'internal' &&
    error.message === 'Could not reach the server'
  );
}

function isNotFoundError(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'not_found';
}

function cardShareVisibility(
  visibility: BoardContentsBoard['visibility'] | undefined,
): SharePanelItem['visibility'] {
  if (visibility === 'public') return 'public';
  if (visibility === 'enterprise_visible') return 'enterprise';
  if (visibility === 'workspace_visible') return 'workspace';
  return 'private';
}

function useIsMobileCardViewport(): boolean {
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const query = window.matchMedia('(max-width: 767px)');
    setMobile(query.matches);
    const handleChange = () => setMobile(query.matches);
    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, []);

  return mobile;
}

type BoardContentsCard = BoardContents['lists'][number]['cards'][number];

function isOrdinaryBoardCardSurface(card: BoardContentsCard): boolean {
  return !('surfaceType' in card) || card.surfaceType === 'card';
}

/**
 * Whether a section that can be EMPTY (cover, location, stickers, related
 * cards) has held something on this card while the card back has been open.
 * It decides between the full section in the body and its compact trigger in
 * the "Add to card" row (`docs/design/stage2/card-back.md` D1).
 *
 * ⚠ Sticky on purpose, in one direction only. Adding the first cover moves the
 * section out of the row into the body, which is the point. Removing the last
 * one does NOT move it back while the card back stays open: the section the
 * member is working in keeps its place, its popover trigger and any notice it
 * just showed (`RelatedCardsSection`'s "removed" notice is local state), rather
 * than being unmounted under them. The next opening of the card starts fresh.
 */
function useShownPopulated(populated: boolean, cardId: string): boolean {
  const [seenFor, setSeenFor] = useState<string | null>(populated ? cardId : null);
  useEffect(() => {
    if (populated) setSeenFor(cardId);
  }, [cardId, populated]);
  return populated || seenFor === cardId;
}

/**
 * Hands focus to a section's trigger in the body when the member's OWN add,
 * made from its compact trigger in the "Add to card" row, moves the section
 * into the body (`useShownPopulated`). The compact trigger and its popover
 * unmount in that render, so Radix's focus return aims at a detached node and
 * focus would fall to <body> (or to the dialog container, Radix's fallback),
 * throwing a keyboard member back to the top of the card back.
 *
 * ⚠ Guarded twice, so a collaborator's change arriving over the socket never
 * steals focus: the member's last focus inside the card back's body must have
 * been in the "Add to card" row (`addRowHasFocus`), AND focus must actually be
 * lost — on <body>, detached, or on the card back's own dialog container.
 * A layout effect, so it runs in the same commit that removed the compact
 * trigger, before Radix's deferred fallbacks.
 */
function useFocusPromotedTrigger(
  inAddRow: boolean,
  shownInBody: boolean,
  triggerRef: RefObject<HTMLButtonElement | null>,
  addRowHasFocus: RefObject<boolean>,
): void {
  const wasInAddRow = useRef(inAddRow);
  useLayoutEffect(() => {
    const promoted = wasInAddRow.current && !inAddRow && shownInBody;
    wasInAddRow.current = inAddRow;
    if (!promoted || !addRowHasFocus.current) return;
    const trigger = triggerRef.current;
    if (trigger === null) return;
    const active = document.activeElement;
    const focusLost =
      active === null ||
      active === document.body ||
      !active.isConnected ||
      active === trigger.closest('[role="dialog"]');
    if (!focusLost) return;
    addRowHasFocus.current = false;
    trigger.focus();
  }, [addRowHasFocus, inAddRow, shownInBody, triggerRef]);
}

export interface CardBackProps {
  cardId: string;
  boardId: string;
  workspaceId?: string | undefined;
  boardClosed: boolean;
  canEdit: boolean;
  initialOpenPanel?: 'dates' | undefined;
  /**
   * The comment a `/c/<ref>/com/<n>` address points at — its per-card
   * `comments.number`, not its id (`docs/notes/comment-numbers.md`). The
   * comment section scrolls to it and highlights it, the way it does for a
   * `#comment-<id>` hash.
   */
  targetCommentNumber?: number | undefined;
  boardMembers: readonly BoardMemberWithHistory[];
  currentUserId?: string | undefined;
  /**
   * `isSuperadminSession(session)` — SA-9; the comment delete gate and the
   * Mirror entry read it.
   */
  currentUserIsSuper?: boolean | undefined;
  onFilterByLabel?: ((labelId: string) => void) | undefined;
  /**
   * B60 local override for tests or route experiments. When omitted, the card
   * back uses `useSetCardEvergreen`.
   */
  onToggleEvergreen?: ((agingExempt: boolean) => void) | undefined;
  evergreenBusy?: boolean | undefined;
  powerUpSurfaces?: PowerUpSurfaceSummary | undefined;
  onClose: () => void;
}

export function CardBack({
  cardId,
  boardId,
  workspaceId,
  boardClosed,
  canEdit,
  initialOpenPanel,
  targetCommentNumber,
  boardMembers,
  currentUserId,
  currentUserIsSuper,
  onFilterByLabel,
  onToggleEvergreen,
  evergreenBusy,
  powerUpSurfaces: injectedPowerUpSurfaces,
  onClose,
}: CardBackProps): ReactElement {
  const { t, locale } = useInterfaceLanguage();
  const mobileCardViewport = useIsMobileCardViewport();
  const card = useQuery(cardDetailQueryOptions(cardId));
  const cardTemplate = useQuery({
    ...cardTemplateQueryOptions(cardId),
    enabled: card.data !== undefined,
  });
  const fieldVersions = useQuery(cardFieldVersionsQueryOptions(cardId));
  /**
   * The caller's own unsaved description text, held by the account rather than
   * by this browser — `FE-14.2`, `FR-CARD-003` AC2.
   *
   * ⚠ **Read in `startDescriptionEdit` and NOWHERE else.** The entry sits under
   * the `['cards', cardId]` prefix that `eventBlastRadius` sweeps, so any live
   * frame naming this card refetches it — which is harmless precisely because
   * nothing reconciles on the data changing. An effect that seeded the editor
   * from this query would become a text-clobbering bug that fires only when
   * another member touches the card while you are typing.
   *
   * Gated on a session and on nothing else. The route grants a **reader** the
   * draft deliberately (it answers 200 even on a closed board, because refusing
   * would destroy unsaved text at the moment it most needs keeping), so a
   * viewer demoted mid-edit still reads their own back.
   */
  const storedDescriptionDraft = useQuery({
    queryKey: queryKeys.cards.descriptionDraft(cardId),
    queryFn: () => fetchCardDescriptionDraft(cardId),
    enabled: currentUserId !== undefined,
  });
  const queryClient = useQueryClient();
  const updateDescription = useUpdateCardDescription();
  /**
   * The account-scoped mirror of the local draft. One mutation for both verbs,
   * because the empty string is the one value that must **not** be `PUT`:
   * `cardDescriptionDraftPutSchema` is `.min(1)` (a `NOT NULL` column, so "no
   * draft" is "no row"), and a writer that sent `''` would take a 422 and — a
   * background mirror failing silently, as it should — leave the server holding
   * the PREVIOUS draft, handing a second browser text the person deliberately
   * deleted.
   */
  const { mutate: mirrorDraft } = useMutation({
    mutationFn: (next: string) =>
      next === ''
        ? discardCardDescriptionDraft(cardId)
        : storeCardDescriptionDraft(cardId, next),
    onSuccess: (draft) => {
      queryClient.setQueryData(queryKeys.cards.descriptionDraft(cardId), draft);
    },
  });
  const addMember = useAddCardMember();
  const removeMember = useRemoveCardMember();
  const removeLabel = useRemoveCardLabel();
  const archiveCard = useArchiveCardDetail(boardId);
  const moveCard = useMoveCard(boardId);
  const moveCardToBoard = useMoveCardToBoard(boardId);
  const copyCard = useCopyCard(boardId, t);
  const createCardMirror = useCreateCardMirror(boardId, t);
  const mergeCards = useMergeCards(boardId);
  const markTemplate = useMarkCardTemplate(boardId);
  const unmarkTemplate = useUnmarkCardTemplate(boardId);
  const hideTemplate = useHideCardTemplateFromList(boardId);
  const unhideTemplate = useUnhideCardTemplateFromList(boardId);
  const createFromTemplate = useCreateCardFromTemplate(boardId);
  const convertLinkCard = useConvertLinkCard(boardId, t);
  const restoreCard = useRestoreCard(boardId);
  const deleteCard = useDeleteCard(boardId);
  const setCompleted = useSetCardCompleted();
  const openLists = useQuery(boardListsQueryOptions(boardId, 'open'));
  const boardContents = useQuery(boardContentsQueryOptions(boardId));
  const undoRedo = useUndoRedo(boardId, t);
  const setEvergreen = useSetCardEvergreen();
  const localePreferences = useLocalePreferencesValue();
  const detail = card.data;
  const title =
    detail?.title.trim() === ''
      ? t('cardBack.untitledCard')
      : (detail?.title ?? t('cardBack.titleFallback'));
  const cardAgingMode = boardContents.data?.board.cardAgingMode;
  const cardAgingEnabled = cardAgingMode === 'regular' || cardAgingMode === 'pirate';
  const lastActivityDate =
    detail === undefined ? null : parseCardTimestamp(detail.lastActivityAt);
  const currentBoardSurface = boardContents.data?.lists
    .flatMap((list) => list.cards)
    .find((candidate) => candidate.id === cardId);
  const isLinkCard =
    currentBoardSurface !== undefined &&
    'surfaceType' in currentBoardSurface &&
    currentBoardSurface.surfaceType === 'link_card';
  const openedFromMirrorContext =
    detail !== undefined &&
    detail.boardId !== boardId &&
    boardContents.data?.lists.some((list) =>
      list.cards.some(
        (candidate) =>
          'surfaceType' in candidate &&
          candidate.surfaceType === 'mirror' &&
          candidate.sourceCardId === detail.id,
      ),
    ) === true;
  const canManageArchivedCard = canEdit && !isLinkCard;
  /**
   * SA-9 — KEEP. `POST /cards/:id/mirrors` refuses a caller who holds the
   * source board only through the superadmin arm: a mirror is a standing
   * channel to the destination board's readers. The server's `isViaSuperadmin`
   * also needs "no workspace row", which this component does not know, so it
   * is read as "no board row" alone. That over-reports a superadmin who is a
   * workspace member but not a board member — fail-closed, and the same side
   * `BoardMenuPanel`'s invite-link gate errs on while its workspace list loads.
   */
  const mirrorRefusedViaSuperadmin =
    currentUserIsSuper === true &&
    !boardMembers.some((member) => member.id === currentUserId);
  const archivedReadOnlyReason =
    detail?.archived === true ? t('cardBack.reason.archivedReadOnly') : null;
  const canEditSharedFields = canManageArchivedCard && archivedReadOnlyReason === null;
  const canCheckEmailToCardPermission =
    currentUserId !== undefined &&
    !isLinkCard &&
    !boardClosed &&
    archivedReadOnlyReason === null;
  const emailToCardPermission = useQuery({
    ...commentStreamQueryOptions(cardId, 1),
    enabled: canCheckEmailToCardPermission,
  });
  const canUseEmailToCard =
    canCheckEmailToCardPermission && emailToCardPermission.data?.mayComment === true;
  const emailToCard = useEmailToCardAddress(cardId, canUseEmailToCard);
  const regenerateEmailToCard = useRegenerateEmailToCardAddress(cardId);
  const cardAutomationButtons = useCardAutomationButtons(
    cardId,
    canEditSharedFields && !boardClosed,
  );
  const runCardAutomationButton = useRunCardAutomationButton();
  const boardPowerUpSurfaces = useBoardPowerUpSurfaces(
    boardId,
    currentUserId !== undefined && injectedPowerUpSurfaces === undefined,
  );
  const powerUpSurfaces = useMemo(
    () => injectedPowerUpSurfaces ?? powerUpSurfaceSummary(boardPowerUpSurfaces.data),
    [boardPowerUpSurfaces.data, injectedPowerUpSurfaces],
  );

  /**
   * The query `RelatedCardsSection` itself reads (one cache entry, so no second
   * request), read here only to decide WHERE that section goes: in the body
   * once it holds a relation (or failed to load, so its error notice shows), as
   * a compact trigger in the "Add to card" row when it has settled empty, and
   * nowhere while it is still loading.
   */
  const relatedCards = useQuery(relatedCardsQueryOptions(cardId));
  const coverShown = useShownPopulated(detail?.cover != null, cardId);
  const locationShown = useShownPopulated(detail?.location != null, cardId);
  const stickersShown = useShownPopulated((detail?.stickers.length ?? 0) > 0, cardId);
  const relatedShown = useShownPopulated((relatedCards.data?.length ?? 0) > 0, cardId);
  const relatedSettledEmpty = relatedCards.isSuccess && relatedCards.data.length === 0;
  const showRelatedSection = relatedShown || relatedCards.isError;
  // A reader who cannot edit gets no trigger, so an empty section renders
  // nothing for them at all.
  const addToCardCover = canEditSharedFields && !coverShown;
  const addToCardLocation = canEditSharedFields && !locationShown;
  const addToCardStickers = canEditSharedFields && !stickersShown;
  const addToCardRelated = canEditSharedFields && !relatedShown && relatedSettledEmpty;
  /**
   * Whether the member's last focus inside the card back's body was in the
   * "Add to card" row (its triggers, or a popover one of them opened, which
   * React bubbles through the portal). Set by the row, cleared by the body
   * column, both in the capture phase, so the outer clear runs first.
   */
  const addRowHasFocus = useRef(false);
  const coverTriggerRef = useRef<HTMLButtonElement | null>(null);
  const locationTriggerRef = useRef<HTMLButtonElement | null>(null);
  const stickersTriggerRef = useRef<HTMLButtonElement | null>(null);
  const relatedTriggerRef = useRef<HTMLButtonElement | null>(null);
  useFocusPromotedTrigger(addToCardCover, coverShown, coverTriggerRef, addRowHasFocus);
  useFocusPromotedTrigger(
    addToCardLocation,
    locationShown,
    locationTriggerRef,
    addRowHasFocus,
  );
  useFocusPromotedTrigger(
    addToCardStickers,
    stickersShown,
    stickersTriggerRef,
    addRowHasFocus,
  );
  useFocusPromotedTrigger(
    addToCardRelated,
    showRelatedSection,
    relatedTriggerRef,
    addRowHasFocus,
  );

  const [descriptionEditing, setDescriptionEditing] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [descriptionFieldVersion, setDescriptionFieldVersion] = useState<
    string | undefined
  >(undefined);
  const [descriptionChangedWhileEditing, setDescriptionChangedWhileEditing] =
    useState(false);
  const [fieldOverrideNotice, setFieldOverrideNotice] =
    useState<CardFieldOverridePayload | null>(null);
  const [newActivityEntryIds, setNewActivityEntryIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [queuedDescription, setQueuedDescription] =
    useState<OfflineDescriptionDraft | null>(null);
  const [descriptionQueueLoadedKey, setDescriptionQueueLoadedKey] = useState<
    string | null
  >(null);
  const [deletedQueuedDescription, setDeletedQueuedDescription] = useState<{
    readonly cardTitle: string;
    readonly description: string;
  } | null>(null);
  const [descriptionDiscardPromptOpen, setDescriptionDiscardPromptOpen] = useState(false);
  const [restoreDestinationOpen, setRestoreDestinationOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ArchiveDeleteTarget | null>(null);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveResult, setMoveResult] = useState<{
    reconciliation: Reconciliation;
  } | null>(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyResult, setCopyResult] = useState<Reconciliation | null>(null);
  const [mirrorOpen, setMirrorOpen] = useState(false);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [automationRunReceipt, setAutomationRunReceipt] =
    useState<AutomationButtonRunReceipt | null>(null);
  const cardExport = useQuery({
    ...cardExportQueryOptions(cardId),
    enabled: shareOpen && card.data !== undefined,
  });
  const [sendTyping, setSendTyping] = useState<CardLiveSendMessage | null>(null);
  const [templateCreateOpen, setTemplateCreateOpen] = useState(false);
  const [templateCreateResult, setTemplateCreateResult] = useState<{
    readonly path: string;
  } | null>(null);
  const moveDestinations = useMoveDestinations(
    moveOpen || copyOpen || mirrorOpen,
    currentUserId,
  );
  const [memberPickerOpen, setMemberPickerOpen] = useState(false);
  const [labelPickerOpen, setLabelPickerOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  /**
   * The header "⋯" Actions menu's trigger. Every surface an item of that menu
   * opens (the share panel, and the Move, Copy, Mirror and Merge dialogs)
   * hands focus back here as it closes, because the menu item that opened it
   * no longer exists by then.
   */
  const actionsTriggerRef = useRef<HTMLButtonElement | null>(null);
  /**
   * Set by a menu item that opens a dialog, read once as the menu closes: the
   * `ListHeader` hand-over pattern. A ref rather than state, because it must be
   * readable during Radix's close sequence and must not re-render.
   */
  const actionsHandOverFocus = useRef(false);
  const previousCardLiveStatus = useRef<CardLiveStatus>('disabled');
  const typingIndicators = useCommentTypingIndicators({ cardId, currentUserId });
  const evergreenDisabledReason = !canEdit
    ? t('cardBack.reason.evergreenEditorsOnly')
    : isLinkCard
      ? t('cardBack.reason.convertLinkCardFirst')
      : boardClosed
        ? t('cardBack.reason.boardClosed')
        : null;
  const evergreenDisabledReasonProps =
    evergreenDisabledReason === null ? {} : { disabledReason: evergreenDisabledReason };
  const evergreenBusyValue = evergreenBusy ?? setEvergreen.isPending;
  const evergreenBusyProps = { busy: evergreenBusyValue };
  const templateFacet = cardTemplate.data;
  const templateStateKnown = templateFacet !== undefined;
  const isTemplateCard = templateFacet?.isTemplate === true;
  const boardShortLink = boardContents.data?.board.shortLink;
  const descriptionQueueKey =
    currentUserId === undefined || boardShortLink === undefined
      ? null
      : `${currentUserId}:${boardShortLink}:${cardId}`;
  const showDateAndCompletionControls =
    templateStateKnown && !isTemplateCard && !isLinkCard;
  const draftKey =
    detail === undefined ? null : cardDescriptionDraftKey(detail.id, currentUserId);
  const descriptionDraftMirrorTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelDescriptionDraftMirror = useCallback(() => {
    if (descriptionDraftMirrorTimer.current === null) return;
    clearTimeout(descriptionDraftMirrorTimer.current);
    descriptionDraftMirrorTimer.current = null;
  }, []);

  /** A card back closed mid-pause must not fire its write into a dead tree. */
  useEffect(() => cancelDescriptionDraftMirror, [cancelDescriptionDraftMirror]);

  /**
   * ⚠ **Armed from `onChange` rather than from an effect on `descriptionDraft`.**
   * An effect would also fire on the render that OPENS the editor, mirroring
   * text the person never typed and leaving a draft row behind on every card
   * whose description was merely looked at.
   */
  function scheduleDescriptionDraftMirror(next: string) {
    if (currentUserId === undefined) return;
    cancelDescriptionDraftMirror();
    descriptionDraftMirrorTimer.current = setTimeout(() => {
      descriptionDraftMirrorTimer.current = null;
      mirrorDraft(next);
    }, DESCRIPTION_DRAFT_MIRROR_DEBOUNCE_MS);
  }

  /**
   * ⚠ **Cancel BEFORE the delete, at every call site.** A timer that survives a
   * Save fires afterwards and re-`PUT`s the text that was just saved and
   * cleared — invisible on this browser, and a second browser is then handed
   * back a draft of text that is already the saved description.
   *
   * Memoised, and named in `retryQueuedDescription`'s dependency list, because
   * that callback is itself memoised: a plain function declaration would be
   * captured by a stale render and could clear the mirror against a
   * `currentUserId` the card back no longer has.
   */
  const clearDescriptionDraftMirror = useCallback(() => {
    cancelDescriptionDraftMirror();
    if (currentUserId === undefined) return;
    mirrorDraft('');
  }, [cancelDescriptionDraftMirror, currentUserId, mirrorDraft]);

  function versionForField(field: 'title' | 'description'): string | undefined {
    return fieldVersions.data?.fields.find((version) => version.field === field)?.version;
  }

  function startDescriptionEdit() {
    if (detail === undefined || !canEditSharedFields) return;
    const savedDraft = draftKey === null ? null : window.localStorage.getItem(draftKey);
    /**
     * ⚠ **`??`, never `||`, and local before server.**
     *
     * An empty local draft is a MEANINGFUL value — "this user emptied the
     * description and has not saved yet" — so `''` must win the chain;
     * `||` would fall through and resurrect the server's older text.
     *
     * Local first because on any one browser the local copy is never the older
     * of the two: `onChange` writes `localStorage` synchronously on every
     * keystroke while the mirror write is debounced, and the offline
     * description queue below exists precisely to hold text typed while the
     * network was down. The server copy is the carrier for the SECOND browser,
     * where the local key is absent and the chain falls through to it.
     */
    setDescriptionDraft(
      savedDraft ?? storedDescriptionDraft.data?.draftText ?? detail.description ?? '',
    );
    setDescriptionFieldVersion(versionForField('description'));
    setDescriptionChangedWhileEditing(false);
    setDescriptionEditing(true);
  }

  const descriptionLimitError =
    descriptionDraft.length >= DESCRIPTION_MAX_LENGTH
      ? t('cardBack.description.limitReached')
      : null;
  const descriptionDirty = descriptionDraft !== (detail?.description ?? '');

  function discardDescriptionDraft() {
    if (draftKey !== null) window.localStorage.removeItem(draftKey);
    clearDescriptionDraftMirror();
    setDescriptionEditing(false);
    setDescriptionDiscardPromptOpen(false);
  }

  function requestCancelDescriptionEdit() {
    if (descriptionDirty) {
      setDescriptionDiscardPromptOpen(true);
      return;
    }
    discardDescriptionDraft();
  }

  function requestClose() {
    onClose();
  }

  function saveDescription() {
    if (detail === undefined) return;
    const variables = {
      cardId: detail.id,
      boardId,
      description: descriptionDraft,
      fieldVersion: descriptionFieldVersion,
    };
    updateDescription.mutate(variables, {
      onSuccess: () => {
        if (draftKey !== null) window.localStorage.removeItem(draftKey);
        clearDescriptionDraftMirror();
        setDescriptionEditing(false);
        setQueuedDescription(null);
        setDeletedQueuedDescription(null);
        setDescriptionChangedWhileEditing(false);
        announce(t('cardBack.announce.descriptionSaved'));
      },
      onError: (error) => {
        if (isNetworkSaveError(error)) {
          const queuedAt = new Date().toISOString();
          setQueuedDescription((current) => ({
            clientOperationId:
              current?.clientOperationId ??
              `card-description:${detail.id}:${Date.now().toString(36)}`,
            ...variables,
            cardTitle: title,
            queuedAt: current?.queuedAt ?? queuedAt,
          }));
          return;
        }
        if (isNotFoundError(error)) {
          setDeletedQueuedDescription({
            cardTitle: title,
            description: descriptionDraft,
          });
        }
      },
    });
  }

  const retryQueuedDescription = useCallback(() => {
    if (queuedDescription === null || updateDescription.isPending) return;
    const pending = queuedDescription;
    setQueuedDescription(null);
    updateDescription.mutate(
      {
        cardId: pending.cardId,
        boardId: pending.boardId,
        description: pending.description,
        fieldVersion: pending.fieldVersion,
      },
      {
        onSuccess: () => {
          if (draftKey !== null) window.localStorage.removeItem(draftKey);
          clearDescriptionDraftMirror();
          setDescriptionEditing(false);
          setDeletedQueuedDescription(null);
          setDescriptionChangedWhileEditing(false);
          announce(t('cardBack.announce.queuedDescriptionSaved'));
        },
        onError: (error) => {
          if (isNetworkSaveError(error)) {
            setQueuedDescription({
              ...pending,
              retryAfter: new Date(
                Date.now() + DESCRIPTION_RETRY_BACKOFF_MS,
              ).toISOString(),
            });
            return;
          }
          if (!isNotFoundError(error)) return;
          setDeletedQueuedDescription({
            cardTitle: pending.cardTitle,
            description: pending.description,
          });
        },
      },
    );
  }, [clearDescriptionDraftMirror, draftKey, queuedDescription, updateDescription]);

  const handleLiveFrame = useCallback(
    (frame: LiveSocketFrame) => {
      if (frame.type === 'comment.typing') {
        typingIndicators.receiveTypingFrame(frame);
        return;
      }

      if ('id' in frame && frame.actorUserId !== currentUserId) {
        setNewActivityEntryIds((current) => {
          if (current.has(frame.id)) return current;
          const next = new Set(current);
          next.add(frame.id);
          return next;
        });
      }

      if (
        frame.type === 'comment.added' &&
        frame.cardId === cardId &&
        frame.actorUserId !== null
      ) {
        typingIndicators.clearTypingUser(frame.actorUserId);
      }

      if (frame.type === 'card.field_overridden') {
        const parsed = cardFieldOverridePayloadSchema.safeParse(frame.payload);
        if (parsed.success) {
          setFieldOverrideNotice(parsed.data);
          if (descriptionEditing && parsed.data.field === 'description') {
            setDescriptionChangedWhileEditing(true);
          }
        }
        return;
      }

      if (
        descriptionEditing &&
        frame.type === 'card.description_edited' &&
        frame.actorUserId !== currentUserId
      ) {
        setDescriptionChangedWhileEditing(true);
      }
    },
    [
      cardId,
      currentUserId,
      descriptionEditing,
      typingIndicators.clearTypingUser,
      typingIndicators.receiveTypingFrame,
    ],
  );

  const handleSendReady = useCallback((send: CardLiveSendMessage | null) => {
    setSendTyping(send === null ? null : () => send);
  }, []);

  const handleTypingStateChange = useCallback(
    (typing: boolean) => {
      sendTyping?.(typingMessage(typing ? 'started' : 'stopped'));
    },
    [sendTyping],
  );

  const cardLiveStatus = useCardLive({
    boardId,
    cardId,
    enabled: currentUserId !== undefined,
    onFrame: handleLiveFrame,
    onSendReady: handleSendReady,
  });

  useEffect(() => {
    if (
      descriptionQueueKey === null ||
      currentUserId === undefined ||
      boardShortLink === undefined
    ) {
      return;
    }
    const stored = loadOfflineDescriptionDrafts(currentUserId, boardShortLink).find(
      (draft) => draft.cardId === cardId && draft.boardId === boardId,
    );
    setQueuedDescription((current) => {
      if (current !== null && current.cardId === cardId && current.boardId === boardId) {
        return current;
      }
      return stored ?? null;
    });
    setDescriptionQueueLoadedKey(descriptionQueueKey);
  }, [boardId, boardShortLink, cardId, currentUserId, descriptionQueueKey]);

  useEffect(() => {
    if (
      descriptionQueueKey === null ||
      descriptionQueueLoadedKey !== descriptionQueueKey ||
      currentUserId === undefined ||
      boardShortLink === undefined
    ) {
      return;
    }
    const currentDrafts = loadOfflineDescriptionDrafts(currentUserId, boardShortLink);
    const nextDrafts = currentDrafts.filter(
      (draft) => !(draft.cardId === cardId && draft.boardId === boardId),
    );
    saveOfflineDescriptionDrafts(
      currentUserId,
      boardShortLink,
      queuedDescription === null ? nextDrafts : [...nextDrafts, queuedDescription],
    );
  }, [
    boardId,
    boardShortLink,
    cardId,
    currentUserId,
    descriptionQueueKey,
    descriptionQueueLoadedKey,
    queuedDescription,
  ]);

  useEffect(() => {
    const previous = previousCardLiveStatus.current;
    previousCardLiveStatus.current = cardLiveStatus;
    if (cardLiveStatus !== 'open') return;
    if (previous === 'open' || previous === 'disabled') return;
    if (queuedDescription === null) return;
    if (
      queuedDescription.retryAfter !== undefined &&
      Date.parse(queuedDescription.retryAfter) > Date.now()
    ) {
      return;
    }
    retryQueuedDescription();
  }, [cardLiveStatus, queuedDescription, retryQueuedDescription]);

  function toggleCompleted() {
    if (detail === undefined || !canEditSharedFields || isTemplateCard) return;
    setCompleted.mutate(
      { cardId: detail.id, boardId, completed: !detail.completed },
      {
        onSuccess: (updated) => {
          announce(
            updated.completed
              ? t('cardBack.announce.markedComplete')
              : t('cardBack.announce.markedIncomplete'),
          );
        },
      },
    );
  }

  function toggleEvergreen(nextAgingExempt: boolean) {
    if (detail === undefined || !canEditSharedFields || boardClosed) return;
    if (onToggleEvergreen !== undefined) {
      onToggleEvergreen(nextAgingExempt);
      return;
    }
    setEvergreen.mutate(
      { boardId, cardId: detail.id, agingExempt: nextAgingExempt },
      {
        onSuccess: (updated) => {
          announce(
            updated.agingExempt
              ? t('cardBack.announce.markedEvergreen')
              : t('cardBack.announce.agingRestored'),
          );
        },
      },
    );
  }

  function handleArchiveCard() {
    if (detail === undefined || !canEditSharedFields) return;
    // A menu item cannot show `busy` the way the old button did, so the start
    // is spoken instead; the archived banner is the visible confirmation.
    announce(t('cardBack.actions.archivingBusy'));
    archiveCard.mutate(
      { cardId: detail.id, listId: detail.listId, title },
      {
        onSuccess: () => {
          announce(t('cardBack.announce.archived', { title }));
          void undoRedo.recordSource({
            boardId,
            cardId: detail.id,
            scope: { kind: 'card', cardId: detail.id },
            eventTypes: ['card.archived'],
            label: t('undo.source.cardArchived', { title }),
          });
        },
      },
    );
  }

  function closeSharePanel() {
    setShareOpen(false);
    const restoreFocus = () => actionsTriggerRef.current?.focus();
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(restoreFocus);
    } else {
      restoreFocus();
    }
  }

  function handleRunCardAutomationButton(commandId: string): void {
    setAutomationRunReceipt(null);
    runCardAutomationButton.mutate(
      { cardId, commandId, workspaceId: boardContents.data?.board.workspaceId },
      {
        onSuccess: (receipt) => {
          setAutomationRunReceipt(receipt);
        },
      },
    );
  }

  function handlePowerUpCardBackButton(entry: PowerUpSurfaceRuntimeEntry): void {
    announce(
      t('cardBack.powerUp.notInteractiveAnnouncement', {
        powerUp: entry.powerUpName,
        label: entry.label,
      }),
    );
  }

  function powerUpAuthorizationPrompt(entry: PowerUpSurfaceRuntimeEntry): string | null {
    if (entry.authorizationState === 'connect_required') {
      return t('cardBack.powerUp.connect', { powerUp: entry.powerUpName });
    }
    if (entry.authorizationState === 'reconnect_required') {
      return t('cardBack.powerUp.reconnect', { powerUp: entry.powerUpName });
    }
    return null;
  }

  function handleMoveCard(input: {
    boardId: string;
    listId: string;
    previousCardId: string | null;
    nextCardId: string | null;
    sameBoard: boolean;
  }) {
    if (detail === undefined) return;
    if (input.sameBoard) {
      moveCard.mutate(
        {
          cardId: detail.id,
          fromListId: detail.listId,
          toListId: input.listId,
          previousCardId: input.previousCardId,
          nextCardId: input.nextCardId,
        },
        {
          onSuccess: () => {
            void queryClient.invalidateQueries({
              queryKey: queryKeys.cards.detail(detail.id),
            });
            announce(t('cardBack.announce.moved', { title }));
            void undoRedo.recordSource({
              boardId,
              cardId: detail.id,
              scope: { kind: 'card', cardId: detail.id },
              eventTypes:
                detail.listId === input.listId
                  ? ['card.position_changed']
                  : ['card.moved_between_lists'],
              label: t('undo.source.cardMoved', { title }),
            });
            setMoveOpen(false);
            setMoveResult(null);
          },
        },
      );
      return;
    }

    moveCardToBoard.mutate(
      {
        cardId: detail.id,
        title,
        sourceBoardId: boardId,
        sourceListId: detail.listId,
        destinationBoardId: input.boardId,
        destinationListId: input.listId,
        previousCardId: input.previousCardId,
        nextCardId: input.nextCardId,
      },
      {
        onSuccess: (result) => {
          announce(t('cardBack.announce.moved', { title }));
          setMoveResult({ reconciliation: result.reconciliation });
        },
      },
    );
  }

  function handleMoveCardPreview(input: {
    boardId: string;
    listId: string;
    previousCardId: string | null;
    nextCardId: string | null;
  }): Promise<Reconciliation> {
    if (detail === undefined) {
      return Promise.reject(new Error('No card loaded for move preview'));
    }
    return queryClient.fetchQuery({
      queryKey: queryKeys.cards.movePreview(detail.id, input.boardId, input.listId),
      queryFn: () =>
        previewCardMoveToBoard(detail.id, {
          boardId: input.boardId,
          listId: input.listId,
        }),
    });
  }

  function handleCopyCard(input: {
    boardId: string;
    listId: string;
    previousCardId: string | null;
    nextCardId: string | null;
    title: string;
    keepChecklists: boolean;
    keepLabels: boolean;
    keepMembers: boolean;
    keepAttachments: boolean;
    keepDates: boolean;
  }) {
    if (detail === undefined) return;
    copyCard.mutate(
      {
        sourceBoardId: boardId,
        sourceCardId: detail.id,
        sourceTitle: title,
        destinationBoardId: input.boardId,
        destinationListId: input.listId,
        previousCardId: input.previousCardId,
        nextCardId: input.nextCardId,
        title: input.title,
        keepChecklists: input.keepChecklists,
        keepLabels: input.keepLabels,
        keepMembers: input.keepMembers,
        keepAttachments: input.keepAttachments,
        keepDates: input.keepDates,
      },
      {
        onSuccess: (result) => {
          announce(t('cardBack.announce.copied', { title: result.card.title }));
          setCopyResult(result.reconciliation);
          if (!hasReconciliationConsequences(result.reconciliation)) {
            setCopyOpen(false);
            setCopyResult(null);
          }
        },
      },
    );
  }

  function handleMirrorCard(input: {
    boardId: string;
    boardTitle: string;
    listId: string;
    listTitle: string;
    previousSurfaceId: string | null;
    nextSurfaceId: string | null;
  }) {
    if (detail === undefined || mirrorRefusedViaSuperadmin) return;
    createCardMirror.mutate(
      {
        cardId: detail.id,
        title,
        sourceBoardTitle: boardContents.data?.board.title ?? 'source board',
        listId: input.listId,
        destinationBoardId: input.boardId,
        destinationBoardTitle: input.boardTitle,
        destinationListTitle: input.listTitle,
        previousSurfaceId: input.previousSurfaceId,
        nextSurfaceId: input.nextSurfaceId,
      },
      {
        onSuccess: () => setMirrorOpen(false),
      },
    );
  }

  function handleMergeCard(sourceCard: CardMergeCandidate) {
    if (
      detail === undefined ||
      !canEditSharedFields ||
      isTemplateCard ||
      boardClosed ||
      detail.list.archived
    ) {
      return;
    }

    mergeCards.mutate(
      {
        targetCardId: detail.id,
        sourceCardId: sourceCard.id,
        targetTitle: title,
        sourceTitle: sourceCard.title,
      },
      {
        onSuccess: (result) => {
          setMergeOpen(false);
          announce(
            t('cardBack.announce.merged', {
              title,
              source: sourceCard.title,
              result: result.card.title,
            }),
          );
          void undoRedo.recordSource({
            boardId,
            cardId: result.card.id,
            activityEntryId: result.activityEntryId,
            scope: { kind: 'card', cardId: result.card.id },
            eventTypes: ['card.merged'],
            label: t('undo.source.cardsMerged', {
              target: title,
              source: sourceCard.title,
            }),
          });
          onClose();
        },
      },
    );
  }

  function handleMarkTemplate() {
    if (detail === undefined || !canEditSharedFields) return;
    markTemplate.mutate(
      { cardId: detail.id, title },
      { onSuccess: () => announce(t('cardBack.announce.markedTemplate', { title })) },
    );
  }

  function handleUnmarkTemplate() {
    if (detail === undefined || !canEditSharedFields) return;
    unmarkTemplate.mutate(
      { cardId: detail.id, title },
      {
        onSuccess: () => {
          announce(t('cardBack.announce.unmarkedTemplate', { title }));
          setTemplateCreateOpen(false);
          setTemplateCreateResult(null);
        },
      },
    );
  }

  function handleHideTemplateFromList(cardId: string) {
    if (!canEditSharedFields) return;
    hideTemplate.mutate(
      { cardId, title },
      {
        onSuccess: () =>
          announce(t('cardBack.announce.templateHiddenFromList', { title })),
      },
    );
  }

  function handleUnhideTemplateFromList(cardId: string) {
    if (!canEditSharedFields) return;
    unhideTemplate.mutate(
      { cardId, title },
      {
        onSuccess: () => announce(t('cardBack.announce.templateShownInList', { title })),
      },
    );
  }

  function handleCreateFromTemplate(input: {
    readonly templateCardId: string;
    readonly title: string;
  }) {
    if (detail === undefined) return;
    createFromTemplate.mutate(
      {
        boardId,
        templateCardId: input.templateCardId,
        templateTitle: title,
        destinationListId: detail.listId,
        previousCardId: null,
        nextCardId: null,
        title: input.title,
      },
      {
        onSuccess: (result) => {
          announce(
            t('cardBack.announce.createdFromTemplate', { title: result.card.title }),
          );
          setTemplateCreateResult({ path: result.card.path });
        },
      },
    );
  }

  function handleRestoreCard(listId?: string) {
    if (detail === undefined || !canManageArchivedCard) return;
    restoreCard.mutate(
      { cardId: detail.id, listId },
      {
        onSuccess: () => {
          announce(t('cardBack.announce.restored', { title }));
          setRestoreDestinationOpen(false);
        },
      },
    );
  }

  function handleDeleteCard() {
    if (deleteTarget?.type !== 'card') return;
    if (!canManageArchivedCard) return;
    deleteCard.mutate(
      { cardId: deleteTarget.id, title: deleteTarget.title },
      {
        onSuccess: () => {
          announce(t('cardBack.announce.deleted', { title: deleteTarget.title }));
          setDeleteTarget(null);
          onClose();
        },
      },
    );
  }

  function toggleMember(userId: string, selected: boolean) {
    if (detail === undefined) return;
    const variables = { cardId: detail.id, boardId, userId };
    if (selected) {
      removeMember.mutate(variables, {
        onSuccess: () => {
          void undoRedo.recordSource({
            boardId,
            cardId: detail.id,
            scope: { kind: 'card', cardId: detail.id },
            eventTypes:
              userId === currentUserId ? ['member.self_left'] : ['member.removed'],
            label: t('undo.source.memberRemoved'),
          });
        },
      });
    } else {
      addMember.mutate(variables, {
        onSuccess: () => {
          void undoRedo.recordSource({
            boardId,
            cardId: detail.id,
            scope: { kind: 'card', cardId: detail.id },
            eventTypes:
              userId === currentUserId ? ['member.self_joined'] : ['member.added'],
            label: t('undo.source.memberAdded'),
          });
        },
      });
    }
  }

  const filteredMembers = useMemo(() => {
    const query = memberSearch.trim().toLocaleLowerCase();
    if (query === '') return boardMembers;
    return boardMembers.filter((member) =>
      [member.displayName, member.username, profileName(member)]
        .join(' ')
        .toLocaleLowerCase()
        .includes(query),
    );
  }, [boardMembers, memberSearch]);

  const currentUserOnCard =
    currentUserId !== undefined &&
    detail?.members.some((member) => member.id === currentUserId) === true;
  const relatedCardOptions = useMemo<readonly RelatedCardOption[]>(() => {
    return (boardContents.data?.lists ?? []).flatMap((list) =>
      list.cards
        .filter((card) => card.id !== cardId)
        .map((card) => ({
          id: card.id,
          title: card.title.trim() === '' ? t('cardBack.untitledCard') : card.title,
          boardTitle: boardContents.data?.board.title,
          listTitle: list.title,
        })),
    );
  }, [boardContents.data, cardId]);
  const mergeCardOptions = useMemo<readonly CardMergeCandidate[]>(() => {
    return (boardContents.data?.lists ?? [])
      .filter((list) => !list.archived)
      .flatMap((list) =>
        list.cards
          .filter(
            (card) =>
              card.id !== cardId && !card.archived && isOrdinaryBoardCardSurface(card),
          )
          .map((card) => ({
            id: card.id,
            title: card.title.trim() === '' ? t('cardBack.untitledCard') : card.title,
            listTitle: list.title,
          })),
      );
  }, [boardContents.data, cardId]);

  function handOverFocusThen(open: () => void): () => void {
    return () => {
      actionsHandOverFocus.current = true;
      open();
    };
  }

  /**
   * The header "⋯" Actions menu (`02-card-detail.md`'s layout sentence; D1).
   *
   * Each item carries EXACTLY the condition its inline button carried before
   * it moved here, and an item the viewer may not use is omitted, never
   * disabled (`Menu`'s rule, `FR-CARD-028` AC3). `disabled` is used only for
   * "momentarily unavailable": a mutation already in flight. The whole menu is
   * absent on an archived card, as the action row it replaces was: the
   * archived banner's Send to board and Delete are that card's actions.
   */
  const actionsMenuSections: MenuSection[] =
    detail === undefined || detail.archived
      ? []
      : [
          {
            id: 'share',
            items: [
              {
                id: 'share',
                label: t('cardBack.actions.share'),
                onSelect: () => setShareOpen((open) => !open),
              },
            ],
          },
          {
            id: 'transfer',
            items:
              canEdit && !isLinkCard
                ? [
                    {
                      id: 'move',
                      label: t('cardBack.actions.move'),
                      onSelect: handOverFocusThen(() => {
                        setMoveResult(null);
                        setMoveOpen(true);
                      }),
                    },
                    {
                      id: 'copy',
                      label: t('cardBack.actions.copyAriaLabel'),
                      onSelect: handOverFocusThen(() => {
                        setCopyResult(null);
                        setCopyOpen(true);
                      }),
                    },
                    ...(mirrorRefusedViaSuperadmin
                      ? []
                      : [
                          {
                            id: 'mirror',
                            label: t('cardBack.actions.mirror'),
                            onSelect: handOverFocusThen(() => setMirrorOpen(true)),
                          },
                        ]),
                    ...(isTemplateCard
                      ? []
                      : [
                          {
                            id: 'merge',
                            label: t('cardBack.actions.merge'),
                            onSelect: handOverFocusThen(() => setMergeOpen(true)),
                          },
                        ]),
                  ]
                : [],
          },
          {
            id: 'template',
            items:
              canEdit && !isLinkCard && templateStateKnown
                ? [
                    isTemplateCard
                      ? {
                          id: 'unmark-template',
                          label: t('cardBack.actions.unmarkTemplate'),
                          disabled: unmarkTemplate.isPending,
                          onSelect: handleUnmarkTemplate,
                        }
                      : {
                          id: 'make-template',
                          label: t('cardBack.actions.makeTemplate'),
                          disabled: markTemplate.isPending,
                          onSelect: handleMarkTemplate,
                        },
                  ]
                : [],
          },
          {
            id: 'archive',
            // ⚠ `canEdit` alone, as the old button's render condition was —
            // including on a link card, where `handleArchiveCard`'s own
            // `canEditSharedFields` guard makes it a no-op. Mirrored, not fixed.
            items: canEdit
              ? [
                  {
                    id: 'archive',
                    label: t('cardBack.actions.archive'),
                    disabled: archiveCard.isPending,
                    onSelect: handleArchiveCard,
                  },
                ]
              : [],
          },
        ].filter((section) => section.items.length > 0);

  /*
   * Owner-directed: the open card's header and frame wear the colour of the
   * list it is in — the card's CURRENT list (`detail.listId`), so a move
   * recolours them, looked up in this component's own unfiltered board
   * contents. The panel carries the column's OWN background and border strings
   * from `listColourStyle`; the body stays plain. The rules that paint them,
   * and the dark theme's half, are `[data-card-back-list-colour]` in
   * `styles.css`.
   */
  const listColour =
    detail === undefined
      ? undefined
      : boardContents.data?.lists.find((list) => list.id === detail.listId)?.colour;
  const listSurface = listColour === undefined ? undefined : listColourStyle(listColour);
  const listSurfaceStyle =
    listColour === undefined || listSurface === undefined
      ? undefined
      : ({
          '--card-back-list-bg': listSurface.backgroundColor,
          '--card-back-list-border': listSurface.borderColor,
          '--card-back-list-colour': listColourVar(listColour),
        } as CSSProperties);

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) requestClose();
      }}
      data-card-back-list-colour={listSurfaceStyle === undefined ? undefined : listColour}
      style={listSurfaceStyle}
      size="lg"
      title={
        detail === undefined ? (
          <Skeleton shape="line" width="18rem" />
        ) : (
          <div className="flex items-start gap-2">
            {templateStateKnown ? (
              showDateAndCompletionControls ? (
                <IconButton
                  label={
                    detail.completed
                      ? t('cardBack.completion.markIncomplete')
                      : t('cardBack.completion.markComplete')
                  }
                  size="sm"
                  busy={setCompleted.isPending}
                  disabled={!canEditSharedFields}
                  onClick={toggleCompleted}
                  // The 28px hit area is 6px wider than the glyph on each side: -ml-1.5
                  // puts the glyph on the dialog's content edge (where the meta row
                  // starts), -mr-1 closes the gap to the title, and -mt-0.5 centres it
                  // on the title's first line (text-xl leading-tight is 25px).
                  className={cn(
                    '-mt-0.5 -mr-1 -ml-1.5 shrink-0',
                    detail.completed ? 'text-ok' : 'text-ink-muted',
                  )}
                >
                  {detail.completed ? (
                    <CheckCircle2 aria-hidden="true" className="size-4" />
                  ) : (
                    <Circle aria-hidden="true" className="size-4" />
                  )}
                </IconButton>
              ) : (
                <span className="mt-1 inline-flex rounded-full border border-accent/35 bg-accent-soft px-2 py-0.5 text-xs font-semibold text-accent">
                  {t('cardBack.templateBadge')}
                </span>
              )
            ) : null}
            <div className="min-w-0 flex-1">
              <InlineEdit
                value={title}
                label={t('cardBack.title.label')}
                multiline
                multilineEnterKey="commit"
                readOnly={!canEditSharedFields}
                onCommit={async (next) => {
                  const updated = await renameCard(
                    detail.id,
                    { title: next },
                    { fieldVersion: versionForField('title') },
                  );
                  queryClient.setQueryData(queryKeys.cards.detail(detail.id), {
                    ...detail,
                    title: updated.title,
                    updatedAt: updated.updatedAt,
                  });
                  void queryClient.invalidateQueries({
                    queryKey: queryKeys.boards.contents(boardId),
                  });
                  void queryClient.invalidateQueries({
                    queryKey: queryKeys.cards.fieldVersions(detail.id),
                  });
                  announce(t('cardBack.announce.cardRenamed', { title: updated.title }));
                  void undoRedo.recordSource({
                    boardId,
                    cardId: detail.id,
                    scope: { kind: 'card', cardId: detail.id },
                    eventTypes: ['card.renamed'],
                    label: t('undo.source.cardRenamed', { title: updated.title }),
                  });
                }}
                controlClassName="font-display text-xl leading-tight"
              />
              {cardAgingEnabled ? (
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-ink-muted">
                  <time dateTime={lastActivityDate?.toISOString()}>
                    {t('cardBack.aging.lastUpdated', {
                      date: formatLocaleDateTimeLabel(
                        t,
                        detail.lastActivityAt,
                        localePreferences,
                      ),
                    })}
                  </time>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    role="switch"
                    aria-checked={detail.agingExempt}
                    aria-label={
                      detail.agingExempt
                        ? t('cardBack.aging.evergreenOnAriaLabel')
                        : t('cardBack.aging.evergreenOffAriaLabel')
                    }
                    disabled={evergreenDisabledReason !== null}
                    className={cn(
                      'h-6 min-h-6 px-2 text-xs',
                      detail.agingExempt && 'border-accent bg-accent text-accent-ink',
                    )}
                    iconStart={<Leaf aria-hidden="true" className="size-3.5" />}
                    onClick={() => toggleEvergreen(!detail.agingExempt)}
                    {...evergreenDisabledReasonProps}
                    {...evergreenBusyProps}
                  >
                    {detail.agingExempt
                      ? t('cardBack.aging.evergreenOn')
                      : t('cardBack.aging.evergreenOff')}
                  </Button>
                </div>
              ) : null}
            </div>
          </div>
        )
      }
      description={
        detail === undefined
          ? undefined
          : t('cardBack.dialog.description', { list: detail.list.title })
      }
      /*
       * docs/notes/card-keys.md — the reference beside the title, in the
       * header but outside the accessible name (see `Dialog`'s `headerAside`).
       * `ref` is null for an inbox card and for a viewer outside the reference
       * scope, and then nothing renders: a reference they could copy would not
       * open for them.
       */
      headerAside={
        detail?.ref == null ? undefined : <CardReferenceCopy reference={detail.ref} />
      }
      headerActions={
        actionsMenuSections.length === 0 ? undefined : (
          <Menu
            align="end"
            trigger={
              <IconButton
                ref={actionsTriggerRef}
                label={t('cardBack.actions.menu')}
                size="sm"
              >
                <MoreHorizontal aria-hidden="true" className="size-4" />
              </IconButton>
            }
            sections={actionsMenuSections}
            onCloseAutoFocus={(event) => {
              // Default (focus back to this trigger) for every item that acts
              // in place, Share included: the share panel moves focus nowhere
              // on open, so cancelling the return would drop it to <body>.
              // Cancelled only for the items that open a dialog, which take
              // focus themselves and give it back here through `returnFocus`.
              if (!actionsHandOverFocus.current) return;
              actionsHandOverFocus.current = false;
              event.preventDefault();
            }}
          />
        )
      }
      closeControl={
        <IconButton label={t('cardBack.close')} size="sm">
          <X aria-hidden="true" className="size-4" />
        </IconButton>
      }
    >
      {card.isError ? (
        <EmptyState
          tone="error"
          size="panel"
          title={t('cardBack.error.title')}
          body={t('cardBack.error.body')}
          action={
            <Button size="sm" onClick={() => void card.refetch()}>
              {t('cardBack.error.retry')}
            </Button>
          }
        />
      ) : detail === undefined ? (
        <CardBackSkeleton />
      ) : (
        <div
          className="flex flex-col gap-5"
          onFocusCapture={() => {
            addRowHasFocus.current = false;
          }}
        >
          {/*
            A healthy connection is no longer a full banner above the card's
            content (D1 amended, 2026-10-09): it is a one-line status in the
            state row below. Only a degraded connection earns a banner.
          */}
          {cardLiveStatus === 'reconnecting' || cardLiveStatus === 'closed' ? (
            <Notice
              tone="warn"
              title={t('cardBack.live.reconnecting.title')}
              body={t('cardBack.live.reconnecting.body')}
            />
          ) : cardLiveStatus === 'refused' ? (
            <Notice
              tone="bad"
              title={t('cardBack.live.refused.title')}
              body={t('cardBack.live.refused.body')}
            />
          ) : null}

          {queuedDescription !== null ? (
            <CardConflictNotice
              title={t('cardBack.description.queued.title', {
                card: queuedDescription.cardTitle,
              })}
              body={t('cardBack.description.queued.body')}
              action={
                <div className="flex flex-wrap items-center gap-3">
                  <Button
                    size="sm"
                    variant="secondary"
                    busy={updateDescription.isPending}
                    onClick={retryQueuedDescription}
                  >
                    {t('cardBack.description.queued.retry')}
                  </Button>
                  {queuedDescription.retryAfter !== undefined ? (
                    <span className="text-xs text-ink-muted">
                      {t('cardBack.description.queued.retryPaused', {
                        time: new Intl.DateTimeFormat(locale, {
                          hour: 'numeric',
                          minute: '2-digit',
                          second: '2-digit',
                        }).format(new Date(queuedDescription.retryAfter)),
                      })}
                    </span>
                  ) : null}
                </div>
              }
            />
          ) : null}

          {deletedQueuedDescription !== null ? (
            <CardConflictNotice
              title={t('cardBack.description.deleted.title', {
                card: deletedQueuedDescription.cardTitle,
              })}
              body={
                <span>
                  {t('cardBack.description.deleted.body')}
                  <span className="font-medium text-ink">
                    {deletedQueuedDescription.description}
                  </span>
                </span>
              }
            />
          ) : null}

          {fieldOverrideNotice !== null ? (
            <CardConflictNotice
              title={t(
                fieldOverrideNotice.field === 'title'
                  ? 'cardBack.fieldOverride.title.title'
                  : 'cardBack.fieldOverride.title.description',
              )}
              body={
                fieldOverrideNotice.overriddenValue ??
                t('cardBack.fieldOverride.emptyBody')
              }
            />
          ) : null}

          {detail.list.archived ? (
            <Notice
              tone="warn"
              title={t('cardBack.archivedList.title')}
              body={t('cardBack.archivedList.body', { list: detail.list.title })}
            />
          ) : null}

          {detail.archived ? (
            <Notice
              tone="warn"
              title={t('cardBack.archived.title')}
              body={
                openedFromMirrorContext
                  ? t('cardBack.archived.mirrorBody')
                  : t('cardBack.archived.body')
              }
              action={
                canManageArchivedCard && !openedFromMirrorContext ? (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      busy={restoreCard.isPending}
                      busyLabel={t('cardBack.archived.restoringBusy')}
                      onClick={() => {
                        if (detail.list.archived) setRestoreDestinationOpen(true);
                        else handleRestoreCard();
                      }}
                    >
                      {t('cardBack.archived.sendToBoard')}
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      onClick={() =>
                        setDeleteTarget({ type: 'card', id: detail.id, title })
                      }
                    >
                      {t('cardBack.archived.delete')}
                    </Button>
                  </div>
                ) : null
              }
            />
          ) : null}

          {isLinkCard ? (
            <Notice
              tone="locked"
              title={t('cardBack.linkCard.title')}
              body={t('cardBack.linkCard.body')}
            />
          ) : null}

          {detail.archived ? null : (
            /*
             * State, not actions, so these stay inline rather than in the "⋯"
             * menu: the watch toggle IS the watching indicator
             * (`FR-COLLAB-022`), a link card's one way forward is to convert
             * it, a template card's is to use it, and the hide-from-list
             * toggle states a refusal with `disabledReason`, which a menu item
             * cannot carry (`Menu`'s omit-never-disable rule).
             */
            <div className="flex flex-wrap items-center gap-2">
              <WatchToggle
                target={{ targetType: 'card', targetId: detail.id }}
                label={title}
                description={t('cardBack.watch.description')}
                descriptionDisplay="described"
                enabled={currentUserId !== undefined}
              />
              {cardLiveStatus === 'open' ? (
                <p
                  role="status"
                  className="ml-auto inline-flex items-center gap-1.5 text-xs text-ink-muted"
                >
                  <span aria-hidden="true" className="size-1.5 rounded-full bg-ok" />
                  <span>{t('cardBack.live.open.title')}</span>
                  <span className="sr-only">{t('cardBack.live.open.body')}</span>
                </p>
              ) : null}
              {canEdit && isLinkCard ? (
                <Button
                  size="sm"
                  variant="primary"
                  busy={convertLinkCard.isPending}
                  onClick={() => convertLinkCard.mutate({ cardId: detail.id, title })}
                >
                  {t('cardBack.actions.convertLink')}
                </Button>
              ) : null}
              {canEdit && !isLinkCard && templateStateKnown && isTemplateCard ? (
                <>
                  <Button
                    size="sm"
                    variant="primary"
                    iconStart={<CopyPlus aria-hidden="true" className="size-4" />}
                    onClick={() => {
                      setTemplateCreateResult(null);
                      setTemplateCreateOpen(true);
                    }}
                  >
                    {t('cardBack.actions.createFromTemplate')}
                  </Button>
                  <TemplateCardVisibilityAction
                    template={templateFacet}
                    canManage={canEditSharedFields}
                    disabledReason={t('cardBack.reason.editorHideTemplate')}
                    busy={hideTemplate.isPending || unhideTemplate.isPending}
                    onHide={handleHideTemplateFromList}
                    onUnhide={handleUnhideTemplateFromList}
                    className="basis-full"
                  />
                </>
              ) : null}
            </div>
          )}

          {shareOpen ? (
            <SharePanel
              item={{
                kind: 'card',
                title,
                path: detail.path,
                reference: detail.ref,
                visibility: cardShareVisibility(boardContents.data?.board.visibility),
                embedPath: cardEmbedUrl(
                  detail.id,
                  typeof window === 'undefined'
                    ? 'http://localhost'
                    : window.location.origin,
                ),
                exportAvailable: true,
                exportJson: cardExport.data,
                exportBusy: cardExport.isPending,
                exportDisabledReason:
                  cardExport.isError && cardExport.error instanceof Error
                    ? cardExport.error.message
                    : undefined,
                printView:
                  cardExport.data === undefined ? undefined : (
                    <CardPrintView exportData={cardExport.data} />
                  ),
                printBusy: cardExport.isPending,
                printDisabledReason:
                  cardExport.isError && cardExport.error instanceof Error
                    ? cardExport.error.message
                    : undefined,
              }}
              origin={typeof window === 'undefined' ? '' : window.location.origin}
              onClose={closeSharePanel}
              mobileDeclaration={
                mobileCardViewport
                  ? {
                      title: t('cardBack.mobile.share.title'),
                      body: t('cardBack.mobile.share.body'),
                      href:
                        typeof window === 'undefined'
                          ? detail.path
                          : window.location.href,
                    }
                  : undefined
              }
            />
          ) : null}

          {shareOpen || detail.archived ? (
            /*
             * Email-to-card sits under Share (`FR-AUTO-031` needs the address
             * reachable on the card back, not permanently on it), where Trello
             * keeps "Email for this card". On an ARCHIVED card there is no
             * Actions menu and so no Share, and the section shows in place,
             * locked with the archived reason, as it did before the layout
             * change (D1 amended, item 4).
             */
            <CardEmailToCardSection
              address={emailToCard.data}
              loading={
                (canCheckEmailToCardPermission && emailToCardPermission.isPending) ||
                (canUseEmailToCard && emailToCard.isPending)
              }
              loadError={
                emailToCardPermission.error?.message ?? emailToCard.error?.message
              }
              canRegenerate={canUseEmailToCard}
              disabledReason={
                archivedReadOnlyReason ??
                (boardClosed
                  ? t('cardBack.reason.emailBoardClosed')
                  : t('cardBack.reason.emailCommenter'))
              }
              regenerateBusy={regenerateEmailToCard.isPending}
              regenerateError={regenerateEmailToCard.error?.message}
              onRegenerate={() => {
                regenerateEmailToCard.mutate(undefined, {
                  onSuccess: () => announce(t('cardBack.announce.emailReset')),
                });
              }}
            />
          ) : null}

          {coverShown ? (
            <CardCoverSection
              card={detail}
              boardId={boardId}
              canEdit={canEditSharedFields}
              triggerRef={coverTriggerRef}
            />
          ) : null}

          {/*
           * The META row (D1): who, which labels, when, and votes, as a
           * wrapping row of compact blocks above the description.
           */}
          <div className="flex flex-wrap gap-x-6 gap-y-3">
            <section
              aria-labelledby="card-members-heading"
              className="flex min-w-0 max-w-full flex-col gap-2"
            >
              <div className="flex items-center gap-2">
                <h2 id="card-members-heading" className="text-sm font-semibold text-ink">
                  {t('cardBack.members.heading')}
                </h2>
                <div className="min-w-0 flex-1">
                  {/*
                    `overflowAction="none"`: the default `'popover'` renders a
                    `<button>` that only does something when the caller wires
                    `overflowProps`, and nothing here owns a popover listing the
                    card's members — the member picker beside this row is an
                    editor of the board's roster, absent for a reader. So past
                    eight members the `+N` is the static, spoken chip rather
                    than a tab stop that opens nothing.
                  */}
                  <AvatarStack
                    members={detail.members}
                    size="sm"
                    max={8}
                    names="tooltip"
                    overflowAction="none"
                  />
                </div>
                {canEditSharedFields ? (
                  <Popover
                    open={memberPickerOpen}
                    onOpenChange={setMemberPickerOpen}
                    title={t('cardBack.members.pickerTitle')}
                    width={320}
                    trigger={
                      <IconButton label={t('cardBack.members.edit')} size="sm">
                        <UserPlus aria-hidden="true" className="size-4" />
                      </IconButton>
                    }
                    closeControl={
                      <IconButton label={t('cardBack.members.closePicker')} size="sm">
                        <X aria-hidden="true" className="size-4" />
                      </IconButton>
                    }
                    search={
                      <TextField
                        label={t('cardBack.members.search')}
                        value={memberSearch}
                        onChange={(event) => setMemberSearch(event.currentTarget.value)}
                        wrapperClassName="gap-1"
                      />
                    }
                    footer={
                      currentUserId !== undefined ? (
                        <Button
                          size="sm"
                          variant="quiet"
                          onClick={() => toggleMember(currentUserId, currentUserOnCard)}
                        >
                          {currentUserOnCard
                            ? t('cardBack.members.leave')
                            : t('cardBack.members.join')}
                        </Button>
                      ) : null
                    }
                  >
                    {filteredMembers.length === 0 ? (
                      <EmptyState
                        tone="neutral"
                        size="inline"
                        title={t('cardBack.members.noMatches')}
                      />
                    ) : (
                      <div className="flex flex-col gap-1">
                        {filteredMembers.map((member) => {
                          const selected = detail.members.some(
                            (cardMember) => cardMember.id === member.id,
                          );
                          return (
                            <FacetOption
                              key={member.id}
                              control="checkbox"
                              label={profileName(member)}
                              checked={selected}
                              onChange={() => toggleMember(member.id, selected)}
                              leadingKind="avatar"
                              leading={<Avatar profile={member} size="sm" decorative />}
                            />
                          );
                        })}
                      </div>
                    )}
                  </Popover>
                ) : null}
              </div>
              {detail.members.length === 0 ? (
                <p className="text-sm text-ink-muted">{t('cardBack.members.empty')}</p>
              ) : null}
            </section>

            <section
              aria-labelledby="card-labels-heading"
              className="flex min-w-0 max-w-full flex-col gap-2"
            >
              <div className="flex items-center gap-2">
                <h2 id="card-labels-heading" className="text-sm font-semibold text-ink">
                  {t('cardBack.labels.heading')}
                </h2>
                <div className="min-w-0 flex-1" />
                {canEditSharedFields ? (
                  <LabelPicker
                    boardId={boardId}
                    cardId={detail.id}
                    cardLabels={detail.labels}
                    canEdit={canEditSharedFields}
                    open={labelPickerOpen}
                    onFilterByLabel={onFilterByLabel}
                    onOpenChange={setLabelPickerOpen}
                    onToggleLabelSuccess={(change) => {
                      void undoRedo.recordSource({
                        boardId,
                        cardId: detail.id,
                        scope: { kind: 'card', cardId: detail.id },
                        eventTypes: [change.selected ? 'label.added' : 'label.removed'],
                        label: change.selected
                          ? t('undo.source.labelAdded')
                          : t('undo.source.labelRemoved'),
                      });
                    }}
                  />
                ) : null}
              </div>
              {detail.labels.length === 0 ? (
                <p className="text-sm text-ink-muted">{t('cardBack.labels.empty')}</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {detail.labels.map((label) => (
                    <span key={label.id} className="inline-flex items-center gap-1">
                      <LabelChip
                        colour={label.colour}
                        name={label.name}
                        removable={canEditSharedFields}
                        onRemove={() =>
                          removeLabel.mutate(
                            {
                              boardId,
                              cardId: detail.id,
                              labelId: label.id,
                            },
                            {
                              onSuccess: () => {
                                void undoRedo.recordSource({
                                  boardId,
                                  cardId: detail.id,
                                  scope: { kind: 'card', cardId: detail.id },
                                  eventTypes: ['label.removed'],
                                  label: t('undo.source.labelRemoved'),
                                });
                              },
                            },
                          )
                        }
                        removeLabel={t('cardBack.labels.remove', {
                          label: labelDisplayName(label, t),
                        })}
                      />
                      {onFilterByLabel === undefined ? null : (
                        <Button
                          type="button"
                          size="sm"
                          variant="quiet"
                          className="h-6 min-h-6 shrink-0 whitespace-nowrap px-1.5 text-xs"
                          iconStart={<Search className="size-3" />}
                          aria-label={t('cardBack.labels.filterBy', {
                            label: labelDisplayName(label, t),
                          })}
                          onClick={() => onFilterByLabel(label.id)}
                        >
                          {t('cardBack.labels.filter')}
                        </Button>
                      )}
                    </span>
                  ))}
                </div>
              )}
            </section>

            {showDateAndCompletionControls ? (
              <CardDatesSection
                card={detail}
                boardId={boardId}
                canEdit={canEditSharedFields}
                initialOpen={initialOpenPanel === 'dates'}
              />
            ) : null}

            <CardVoteSection
              cardId={detail.id}
              boardId={boardId}
              boardClosed={boardClosed}
              readOnlyReason={archivedReadOnlyReason ?? undefined}
              hideWhenOff
            />

            <CardApprovalSection
              cardId={detail.id}
              boardId={boardId}
              approvals={detail.approvals}
              currentUserId={currentUserId}
              canWrite={canEditSharedFields && !boardClosed}
            />
          </div>

          <section
            aria-labelledby="card-description-heading"
            className="flex flex-col gap-2"
          >
            <div className="flex items-center justify-between gap-2">
              <h2
                id="card-description-heading"
                className="text-sm font-semibold text-ink"
              >
                {t('cardBack.description.heading')}
              </h2>
              {canEditSharedFields && !descriptionEditing ? (
                <Button size="sm" variant="quiet" onClick={startDescriptionEdit}>
                  {detail.description === null
                    ? t('cardBack.description.add')
                    : t('cardBack.description.edit')}
                </Button>
              ) : null}
            </div>

            {descriptionEditing ? (
              <div className="flex flex-col gap-2">
                {descriptionChangedWhileEditing ? (
                  <CardConflictNotice
                    title={t('cardBack.description.conflict.title')}
                    body={t('cardBack.description.conflict.body')}
                  />
                ) : null}
                <RichDescriptionEditor
                  value={descriptionDraft}
                  maxLength={DESCRIPTION_MAX_LENGTH}
                  warnAt={DESCRIPTION_WARN_AT}
                  error={descriptionLimitError ?? undefined}
                  busy={updateDescription.isPending}
                  onChange={(next) => {
                    setDescriptionDraft(next);
                    if (draftKey !== null) window.localStorage.setItem(draftKey, next);
                    scheduleDescriptionDraftMirror(next);
                  }}
                  onSave={saveDescription}
                  onCancel={requestCancelDescriptionEdit}
                />
                {queuedDescription !== null ? (
                  <p className="text-xs font-medium text-warn">
                    {t('cardBack.description.queued.saveRetryHint')}
                  </p>
                ) : null}
              </div>
            ) : detail.description === null ? (
              <EmptyState
                tone="neutral"
                size="inline"
                title={t('cardBack.description.empty.title')}
                body={
                  canEditSharedFields ? t('cardBack.description.empty.body') : undefined
                }
              />
            ) : (
              <MarkdownBody markdown={detail.description} />
            )}
          </section>

          {/* Only on a board with a visible field definition (card-back.md D1,
              amended; FR-CARD-027 "every card on a board with custom fields").
              `customFields` carries one entry per definition, value or not, so
              a board without any shows no empty "No custom fields" section. */}
          {detail.customFields.some((field) => !field.hidden) ? (
            <CardCustomFieldsSection
              card={detail}
              boardId={boardId}
              canEdit={canEditSharedFields}
            />
          ) : null}

          <CardChecklistSection
            card={detail}
            boardId={boardId}
            canEdit={canEditSharedFields}
            boardMembers={boardMembers}
            currentUserId={currentUserId}
          />

          <CardAttachmentSection
            card={detail}
            boardId={boardId}
            canEdit={canEditSharedFields}
          />

          {showRelatedSection ? (
            <RelatedCardsSection
              cardId={detail.id}
              boardId={boardId}
              canEdit={canEditSharedFields}
              cardOptions={relatedCardOptions}
              triggerRef={relatedTriggerRef}
            />
          ) : null}

          {locationShown ? (
            <CardLocationSection
              card={detail}
              boardId={boardId}
              canEdit={canEditSharedFields}
              triggerRef={locationTriggerRef}
            />
          ) : null}

          {stickersShown ? (
            <CardStickerSection
              card={detail}
              boardId={boardId}
              canEdit={canEditSharedFields}
              triggerRef={stickersTriggerRef}
            />
          ) : null}

          {powerUpSurfaces.cardBackBadges.length > 0 ? (
            <div
              aria-label={t('cardBack.powerUp.badgesLabel')}
              className="flex flex-wrap items-center gap-1.5 text-xs text-ink-muted"
            >
              {powerUpSurfaces.cardBackBadges.map((entry) => (
                <span
                  key={entry.id}
                  aria-label={`${entry.powerUpName}: ${entry.label}`}
                  className="inline-flex max-w-full items-center rounded-[6px] border border-border bg-surface-raised px-1.5 py-0.5 font-medium"
                >
                  <span className="min-w-0 truncate">{entry.label}</span>
                </span>
              ))}
            </div>
          ) : null}

          {powerUpSurfaces.cardBackButtons.length > 0 ? (
            <div
              aria-label={t('cardBack.powerUp.buttonsLabel')}
              className="flex flex-wrap items-center gap-2"
            >
              {powerUpSurfaces.cardBackButtons.map((entry) => (
                <div key={entry.id} className="flex flex-col gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    aria-label={`${entry.powerUpName}: ${entry.label}`}
                    disabled
                    disabledReason={t('cardBack.powerUp.notInteractive', {
                      powerUp: entry.powerUpName,
                      label: entry.label,
                    })}
                    onClick={() => handlePowerUpCardBackButton(entry)}
                  >
                    {entry.label}
                  </Button>
                  {powerUpAuthorizationPrompt(entry) === null ? null : (
                    <p className="text-xs text-ink-muted">
                      {powerUpAuthorizationPrompt(entry)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : null}

          {powerUpSurfaces.cardBackSections.map((entry) => (
            <section
              key={entry.id}
              aria-labelledby={`power-up-card-section-${entry.id}`}
              className="flex flex-col gap-2"
            >
              <h2
                id={`power-up-card-section-${entry.id}`}
                className="text-sm font-semibold text-ink"
              >
                {entry.powerUpName}
              </h2>
              <div className="rounded-card border border-border bg-surface-raised px-3 py-2 text-sm text-ink-muted">
                {entry.emptyState ?? entry.description ?? entry.label}
              </div>
            </section>
          ))}

          <AutomationButtonStrip
            label={t('cardBack.automation.stripLabel')}
            buttons={cardAutomationButtons.data?.buttons ?? []}
            loading={cardAutomationButtons.isPending && canEditSharedFields}
            disabled={!canEditSharedFields}
            disabledReason={t('cardBack.reason.automationEditorsOnly')}
            runBusyId={
              runCardAutomationButton.isPending
                ? (runCardAutomationButton.variables?.commandId ?? null)
                : null
            }
            receipt={automationRunReceipt}
            error={
              cardAutomationButtons.isError && canEditSharedFields
                ? cardAutomationButtons.error.message
                : runCardAutomationButton.error?.message
            }
            onRun={handleRunCardAutomationButton}
          />

          {addToCardCover ||
          addToCardLocation ||
          addToCardStickers ||
          addToCardRelated ? (
            /*
             * The "Add to card" row (D1): the sections that are still EMPTY,
             * as their trigger alone. Each moves into the body once it holds
             * something; see `useShownPopulated`.
             */
            <section
              aria-labelledby="card-add-to-card-heading"
              className="flex flex-col gap-2"
              onFocusCapture={() => {
                addRowHasFocus.current = true;
              }}
            >
              <h2
                id="card-add-to-card-heading"
                className="text-sm font-semibold text-ink"
              >
                {t('cardBack.addToCard.heading')}
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                {addToCardCover ? (
                  <CardCoverSection
                    card={detail}
                    boardId={boardId}
                    canEdit={canEditSharedFields}
                    compact
                  />
                ) : null}
                {addToCardLocation ? (
                  <CardLocationSection
                    card={detail}
                    boardId={boardId}
                    canEdit={canEditSharedFields}
                    compact
                  />
                ) : null}
                {addToCardStickers ? (
                  <CardStickerSection
                    card={detail}
                    boardId={boardId}
                    canEdit={canEditSharedFields}
                    compact
                  />
                ) : null}
                {addToCardRelated ? (
                  <RelatedCardsSection
                    cardId={detail.id}
                    boardId={boardId}
                    canEdit={canEditSharedFields}
                    cardOptions={relatedCardOptions}
                    compact
                  />
                ) : null}
              </div>
            </section>
          ) : null}

          {!isLinkCard ? (
            <CommentSection
              cardId={detail.id}
              boardId={boardId}
              workspaceId={workspaceId}
              boardClosed={boardClosed}
              readOnlyReason={
                detail.archived
                  ? {
                      title: t('cardBack.comments.readOnly.title'),
                      body: t('cardBack.comments.readOnly.body'),
                    }
                  : undefined
              }
              boardMembers={boardMembers}
              cardMembers={detail.members}
              currentUserId={currentUserId}
              currentUserIsSuper={currentUserIsSuper}
              typingUsers={typingIndicators.typingUsers}
              onTypingStateChange={handleTypingStateChange}
              newActivityEntryIds={newActivityEntryIds}
              onAcknowledgeNewActivityEntries={() => setNewActivityEntryIds(new Set())}
              targetCommentNumber={targetCommentNumber}
            />
          ) : null}
        </div>
      )}
      <Dialog
        open={descriptionDiscardPromptOpen}
        onOpenChange={setDescriptionDiscardPromptOpen}
        size="sm"
        title={t('cardBack.description.discard.title')}
        description={t('cardBack.description.discard.description')}
        footer={
          <>
            <Button
              size="sm"
              variant="quiet"
              onClick={() => setDescriptionDiscardPromptOpen(false)}
            >
              {t('cardBack.description.discard.keep')}
            </Button>
            <Button size="sm" variant="primary" onClick={discardDescriptionDraft}>
              {t('cardBack.description.discard.confirm')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-muted">{t('cardBack.description.discard.body')}</p>
      </Dialog>
      <ArchiveDeleteConfirm
        target={deleteTarget}
        busy={deleteCard.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDeleteCard}
      />
      <ArchiveRestoreDestinationDialog
        open={restoreDestinationOpen}
        cardTitle={title}
        lists={openLists.data ?? []}
        busy={restoreCard.isPending}
        onCancel={() => setRestoreDestinationOpen(false)}
        onRestore={handleRestoreCard}
      />
      {detail !== undefined ? (
        <CrossBoardMoveDialog
          open={moveOpen}
          returnFocus={actionsTriggerRef}
          mode="card"
          subjectTitle={title}
          currentBoardId={boardId}
          currentListId={detail.listId}
          currentListTitle={detail.list.title}
          currentCardId={detail.id}
          currentCards={moveDestinations.cardsByListId.get(detail.listId)}
          boards={moveDestinations.boards}
          listsByBoardId={moveDestinations.listsByBoardId}
          cardsByListId={moveDestinations.cardsByListId}
          loading={moveDestinations.loading}
          busy={moveCard.isPending || moveCardToBoard.isPending}
          result={moveResult?.reconciliation}
          onCancel={() => {
            setMoveOpen(false);
            setMoveResult(null);
          }}
          onPreviewCard={handleMoveCardPreview}
          onMoveCard={handleMoveCard}
        />
      ) : null}
      {detail !== undefined ? (
        <CardCopyDialog
          open={copyOpen}
          returnFocus={actionsTriggerRef}
          subjectTitle={title}
          currentBoardId={boardId}
          currentListId={detail.listId}
          currentCardId={detail.id}
          boards={moveDestinations.boards}
          listsByBoardId={moveDestinations.listsByBoardId}
          cardsByListId={moveDestinations.cardsByListId}
          loading={moveDestinations.loading}
          busy={copyCard.isPending}
          result={copyResult}
          onCancel={() => {
            setCopyOpen(false);
            setCopyResult(null);
          }}
          onCopy={handleCopyCard}
        />
      ) : null}
      {detail !== undefined ? (
        <MirrorCardDialog
          open={mirrorOpen}
          returnFocus={actionsTriggerRef}
          subjectTitle={title}
          sourceBoardId={boardId}
          sourceWorkspaceId={boardContents.data?.board.workspaceId}
          boards={moveDestinations.boards}
          listsByBoardId={moveDestinations.listsByBoardId}
          cardsByListId={moveDestinations.cardsByListId}
          loading={moveDestinations.loading}
          busy={createCardMirror.isPending}
          onCancel={() => setMirrorOpen(false)}
          onMirror={handleMirrorCard}
        />
      ) : null}
      {detail !== undefined ? (
        <CardMergeDialog
          open={mergeOpen}
          returnFocus={actionsTriggerRef}
          currentCardTitle={title}
          candidates={mergeCardOptions}
          busy={mergeCards.isPending}
          error={mergeCards.error instanceof Error ? mergeCards.error.message : null}
          onCancel={() => setMergeOpen(false)}
          onMerge={handleMergeCard}
        />
      ) : null}
      {detail !== undefined && templateFacet !== undefined ? (
        <CardTemplateCreateDialog
          open={templateCreateOpen}
          templates={[templateFacet]}
          defaultTemplateCardId={detail.id}
          destinationListTitle={detail.list.title}
          busy={createFromTemplate.isPending}
          createdCardPath={templateCreateResult?.path}
          onCancel={() => {
            setTemplateCreateOpen(false);
            setTemplateCreateResult(null);
          }}
          onCreate={handleCreateFromTemplate}
        />
      ) : null}
    </Dialog>
  );
}

function CardBackSkeleton(): ReactElement {
  return (
    <div className="flex flex-col gap-5">
      <Skeleton shape="block" count={2} />
      <Skeleton shape="line" width="12rem" />
      <Skeleton shape="block" count={6} />
    </div>
  );
}
